import React, { useEffect, useState, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import Question from '../Question';
import type { Answer } from '../../types/questionnaire';

interface Props {
  theme: Answer;
  onChecked: (id: number) => void;
  selectedThemes: Answer[];
  wrappedThemes: Answer[];
  setWrappedThemes: React.Dispatch<React.SetStateAction<Answer[]>>;
}

const Questions = ({ theme, onChecked, selectedThemes, wrappedThemes, setWrappedThemes }: Props) => {
  const [isWrapped, setIsWrapped] = useState(false);
  const [isAtLeastOneItemSelected, setIsAtLeastOneItemSelected] = useState(false);
  const [lineCoords, setLineCoords] = useState<{ y1: number | null; y2: number | null; height: number | null }>({
    y1: null, y2: null, height: null,
  });
  const nodeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const currentThemeSection = document.getElementById('themeSection-' + theme.id);
    const currentThemeSectionLabel = document.getElementById('themeSectionLabel-' + theme.id);
    const marginTop = 24;
    const gapBtwSections = 24;
    const heightOfClosingSectionLabel = 44;

    if (!currentThemeSection || !currentThemeSectionLabel) return;

    const handleResize = () => {
      const currentThemeIndex = selectedThemes.findIndex((selectedTheme) => selectedTheme.id === theme.id);
      let isCurrentThemeLast = false;
      if (currentThemeIndex !== -1) {
        isCurrentThemeLast = currentThemeIndex === selectedThemes.length - 1;
      }
      if (!isCurrentThemeLast) {
        setLineCoords({
          y1: currentThemeSectionLabel.clientHeight / 2 + marginTop,
          y2: currentThemeSection.clientHeight + gapBtwSections + marginTop + currentThemeSectionLabel.clientHeight / 2,
          height: currentThemeSection.clientHeight + gapBtwSections + marginTop + currentThemeSectionLabel.clientHeight / 2,
        });
      } else {
        setLineCoords({
          y1: currentThemeSectionLabel.clientHeight / 2 + marginTop,
          y2: currentThemeSection.clientHeight + gapBtwSections + marginTop + heightOfClosingSectionLabel / 2,
          height: currentThemeSection.clientHeight + gapBtwSections + marginTop + heightOfClosingSectionLabel / 2,
        });
      }
    };

    if (currentThemeSection.classList.contains('hidden')) return;

    const observer = new ResizeObserver(handleResize);
    observer.observe(currentThemeSection);

    return () => { observer.disconnect(); };
  }, [selectedThemes]);

  useEffect(() => {
    if (!wrappedThemes.some((item) => item.id === theme.id)) {
      setIsWrapped(false);
    }
    if (!theme.questions?.some((questionType) => questionType.answers.some((item) => item.checked))) {
      setIsAtLeastOneItemSelected(false);
    }
  }, [theme, wrappedThemes]);

  const wrapTheme = () => {
    setIsWrapped(!isWrapped);
    setWrappedThemes((prev) => {
      const themeExists = prev.some((item) => item.id === theme.id);
      if (themeExists) return prev;
      return [...prev, theme];
    });
    if (theme.questions?.some((questionType) => questionType.answers.some((item) => item.checked))) {
      setIsAtLeastOneItemSelected(true);
    } else {
      setIsAtLeastOneItemSelected(false);
    }
  };

  const unwrapTheme = () => {
    setIsWrapped(!isWrapped);
    setWrappedThemes((prev) => prev.filter((item) => item.id !== theme.id));
  };

  if (!selectedThemes.length) return null;

  return (
    <div id={'themeSection-' + theme.id} className={`relative flex flex-col w-full gap-5 lg:flex-row lg:gap-6 ${selectedThemes.some((item) => item.id === theme.id) ? 'block' : 'hidden'}`}>
      <div id={'themeSectionLabel-' + theme.id} className="flex-none h-fit w-full lg:max-w-[171px] flex flex-col lg:flex-row lg:gap-3 items-center lg:mt-6">
        <span className={`w-full flex-1 bg-white rounded border-2 py-2 px-3 font-bold text-center lg:text-left ${isAtLeastOneItemSelected ? 'border-secondary-green' : 'border-secondary-orange'}`}>
          {theme.title}
        </span>
        <div className={`lg:hidden arrow-down border-t-[16px] ${isAtLeastOneItemSelected ? 'border-secondary-green' : 'border-secondary-orange'}`}></div>
        {isAtLeastOneItemSelected ? (
          <span className="hidden z-10 lg:block w-5 h-5 bg-secondary-green rounded-full p-1">
            <img src="/assets/images/tick.svg" className="w-full" alt="tick" />
          </span>
        ) : (
          <span className="hidden z-10 lg:block h-5 w-5 bg-secondary-orange rounded-full"></span>
        )}
      </div>
      <div className="flex-1 lg:inline-flex">
        <div className="hidden lg:block arrow-left lg:mt-6"></div>
        <div className="flex-1 rounded-lg p-5 bg-white lg:rounded-[20px] lg:p-10">
          <h3>Thema {theme.title}</h3>
          {isWrapped ? (
            <div className="space-y-5 mt-5">
              {isAtLeastOneItemSelected ? (
                <>
                  <p className="text-sm font-bold">Dit zijn de onderwerpen waar jij graag ondersteuning wil hebben</p>
                  <div className="flex flex-wrap gap-2.5 items-center">
                    {theme.questions?.map((question) =>
                      question.answers.filter((answer) => answer.checked).map((answer) => {
                        if (answer.questions && answer.questions.length > 0) {
                          return answer.questions.map((subQuestion) =>
                            subQuestion.answers.filter((subAnswer) => subAnswer.checked).map((selectedItem) => {
                              if (selectedItem.questions && selectedItem.questions.length > 0) {
                                return selectedItem.questions.map((selectedSubItem) => {
                                  return selectedSubItem.answers.map((selectedSubSubItem) => {
                                    if (!selectedSubSubItem.checked) return null;
                                    return (
                                      <span key={selectedSubSubItem.id} className="inline-flex items-center space-x-2 px-2.5 py-1 border-2 border-main rounded-full text-main font-bold">
                                        <i className="fa-solid fa-check"></i>
                                        <span className="break-all">{answer.title}: {selectedSubSubItem.pill}</span>
                                      </span>
                                    );
                                  });
                                });
                              }
                              return (
                                <span key={selectedItem.id} className="inline-flex items-center space-x-2 px-2.5 py-1 border-2 border-main rounded-full text-main font-bold">
                                  <i className="fa-solid fa-check"></i>
                                  <span className="break-all">{answer.title}: {selectedItem.pill}</span>
                                </span>
                              );
                            })
                          );
                        }
                        return (
                          <span key={answer.id} className="inline-flex items-center space-x-2 px-2.5 py-1 border-2 border-main rounded-full text-main font-bold">
                            <i className="fa-solid fa-check"></i>
                            <span className="break-all">{answer.title}</span>
                          </span>
                        );
                      })
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm font-bold">U heeft (nog) niets geselecteerd waar je ondersteuning bij wilt</p>
              )}
              <p className="text-sm font-bold">Klopt dit niet?</p>
              <button className="w-full sm:w-fit btn btn-primary-outline mt-5" onClick={unwrapTheme}>
                <span>Aanpassen</span>
              </button>
            </div>
          ) : (
            <>
              <p className="text-lg max-w-[720px] mt-3">{theme.description}</p>
              <div className="mt-3 space-y-3">
                {theme.questions?.map((item) => <Question key={item.id} question={item} onChecked={onChecked} />)}
              </div>
              <button className="w-full sm:w-fit btn btn-primary mt-5" onClick={wrapTheme}>
                <span>Thema afronden</span>
              </button>
            </>
          )}
        </div>
      </div>
      {selectedThemes.some((item) => item.id === theme.id) && !!lineCoords.y1 && !!lineCoords.y2 && !!lineCoords.height && (
        <CSSTransition nodeRef={nodeRef} in timeout={300} classNames="fade" unmountOnExit appear={true}>
          <svg ref={nodeRef as unknown as React.RefObject<SVGSVGElement>} width="162" height={lineCoords.height} className="hidden lg:block absolute">
            <line x1="162" y1={lineCoords.y1} x2="162" y2={lineCoords.y2} strokeWidth="4px" stroke="#95CF5B" strokeLinecap="round" strokeDasharray="10,10" />
          </svg>
        </CSSTransition>
      )}
    </div>
  );
};

export default Questions;
